import { useTranslation } from "react-i18next";
import { CaptionText, CustomCard } from "../../_components";
import { ParkingSpotType } from "../../_models/ParkingSpot";
import { resolveSpotBannerStatus } from "../_helpers/parkingPermissions";

const BANNER_LABEL_BY_STATUS = {
    FREE: "mod_parking_free",
    PENDING: "mod_parking_pending",
    OCCUPIED: "mod_parking_occupied",
} as const;

const BANNER_BG_BY_STATUS = {
    FREE: "#c8e6c9",
    PENDING: "#fff3cd",
    OCCUPIED: "#ffcdd2",
} as const;

interface Props {
    spot: ParkingSpotType;
    isSelected?: boolean;
    isHolderView?: boolean;
    onClick?: () => void;
}

export function SingleParkingSpot({
    spot,
    isSelected = false,
    isHolderView = false,
    onClick,
}: Props) {
    const { t } = useTranslation();
    const bannerStatus = resolveSpotBannerStatus({
        holderEmail: spot.holderEmail,
        holderUserUid: spot.holderUserUid,
        assignmentsCount: spot.assignments.length,
        maxAssignments: spot.maxAssignments,
    });
    const bannerText = t(BANNER_LABEL_BY_STATUS[bannerStatus]);
    const bannerBg = BANNER_BG_BY_STATUS[bannerStatus];

    const hasWarnings =
        spot.warnings.length > 0 ||
        spot.assignments.some((a) => a.warnings.length > 0);

    return (
        <CustomCard
            selected={isSelected}
            onClick={onClick}
            className="h-100"
            banner={bannerText}
            bannerBackground={bannerBg}
            banner2={t("mod_parking_floor") + " " + spot.floor}
        >
            <div className="d-flex flex-column c-line-height-1 w-100">
                <div className="d-flex align-items-center mb-1">
                    <div
                        className="font-weight-bold text-break flex-grow-1"
                        style={{ fontSize: "1.1rem" }}
                    >
                        {spot.number}
                    </div>
                    {hasWarnings && (
                        <span
                            title={t("mod_parking_warning_tooltip")}
                            className="ml-2"
                            style={{ fontSize: "1rem" }}
                            aria-label={t("mod_parking_warning_tooltip")}
                        >
                            ⚠️
                        </span>
                    )}
                </div>

                {!isHolderView && (
                    <>
                        {spot.holderName ? (
                            <CaptionText className="text-break">
                                {spot.holderName}
                            </CaptionText>
                        ) : !spot.holderEmail ? (
                            <CaptionText className="font-italic">
                                {t("mod_parking_no_holder")}
                            </CaptionText>
                        ) : null}
                        {spot.holderEmail && (
                            <CaptionText className="text-break">
                                {spot.holderEmail}
                            </CaptionText>
                        )}
                    </>
                )}

                {spot.assignments.length === 0 ? (
                    isHolderView && (
                        <CaptionText className="font-italic">
                            {t("mod_parking_no_assignments")}
                        </CaptionText>
                    )
                ) : (
                    <div className={isHolderView ? "" : "mt-1"}>
                        <CaptionText className="font-weight-bold">
                            {t("mod_parking_assignments")}:
                        </CaptionText>
                        {spot.assignments.slice(0, 3).map((a) => (
                            <CaptionText
                                key={a.email}
                                className="text-break"
                            >
                                {a.userName ? `${a.userName} · ${a.email}` : a.email}
                            </CaptionText>
                        ))}
                        {spot.assignments.length > 3 && (
                            <CaptionText className="font-italic">
                                {t("mod_parking_assignments_more", {
                                    count: spot.assignments.length - 3,
                                })}
                            </CaptionText>
                        )}
                    </div>
                )}

                {spot.maxAssignments > 0 && (
                    <CaptionText className="mt-1 font-italic">
                        {spot.assignments.length}/{spot.maxAssignments}
                    </CaptionText>
                )}

                {spot.notes && (
                    <CaptionText className="mt-2 c-line-clamp-3">
                        {spot.notes}
                    </CaptionText>
                )}
            </div>
        </CustomCard>
    );
}
